import { useState, useEffect } from 'react';
import { useAuth } from '@/lib/auth-context';
import * as db from '@/lib/data-service';
import { supabase } from '@/lib/supabase';
import ProfileCard from '@/components/ProfileCard';
import { Star, Lock } from 'lucide-react';
import { toast } from 'sonner';

export default function EkJiv() {
  const { user, profile, refreshProfile } = useAuth();
  const [ekJivProfile, setEkJivProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acted, setActed] = useState(false);
  const [jjUsed, setJjUsed] = useState(true);

  useEffect(() => {
    if (user?.id) db.canUseJaiJinendra(user.id).then(canUse => setJjUsed(!canUse));
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id || !profile) { setLoading(false); return; }
    if (profile.profile_paused) { setLoading(false); return; }
    db.getEkJivMatch(user.id, profile).then(result => {
      if (!result || result.ekJivActedOn) {
        setActed(true);
      } else {
        setEkJivProfile(result);
        db.markEkJivSeen(user.id);
      }
      setLoading(false);
    });
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleJaiJinendra = async () => {
    if (!ekJivProfile) return;
    if (jjUsed) {
      toast('Your Jai Jinendra resets Monday.');
      return;
    }
    await db.useJaiJinendra(user.id);
    await db.createInteraction(user.id, ekJivProfile.id, 'jai_jinendra', null);
    db.markEkJivActedOn(user.id);
    setActed(true);
    setEkJivProfile(null);
    toast('Jai Jinendra sent.');
  };

  return (
    <div className="h-full flex flex-col relative bg-[#FAF8F5]">
      {/* Header */}
      <div className="px-4 pt-5 pb-3 text-center shrink-0 border-b border-[rgba(0,0,0,0.05)] bg-white">
        <h1 className="text-lg font-bold text-[#C857A8]">Ek Jiv</h1>
        <p className="text-xs text-[#A8A5A0]">Your match this week</p>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto hide-scrollbar px-3 pt-4 pb-28">
        {profile?.profile_paused ? (
          <div className="h-full flex flex-col items-center justify-center text-center px-6">
            <p className="text-base font-semibold text-[#1A1814]">Your profile is paused</p>
            <p className="text-sm text-[#7A7672] mt-2">You won't appear in anyone's feed while paused.</p>
            <button
              onClick={async () => {
                await supabase.from('profiles').update({ profile_paused: false }).eq('id', user.id);
                if (refreshProfile) refreshProfile();
              }}
              className="mt-5 px-6 py-2.5 rounded-full bg-[#C857A8] text-white text-sm font-semibold"
            >
              Resume
            </button>
          </div>
        ) : loading ? (
          <div className="h-full flex items-center justify-center">
            <div className="w-8 h-8 rounded-full border-2 border-[#C857A8] border-t-transparent animate-spin" />
          </div>
        ) : (acted || !ekJivProfile) ? (
          <div className="h-full flex flex-col items-center justify-center text-center px-6">
            <Star className="h-12 w-12 text-[#C857A8] mb-4" />
            <p className="text-base font-semibold text-[#1A1814]">You've acted on your Ek Jiv for this week.</p>
            <p className="text-sm text-[#7A7672] mt-2">Come back Monday for your next introduction.</p>
          </div>
        ) : (
          <div className="ring-2 ring-[#C857A8] ring-offset-2 rounded-[20px]">
            <ProfileCard profile={ekJivProfile} onPromptLike={null} />
          </div>
        )}
      </div>

      {/* Floating Jai Jinendra button */}
      {!loading && !acted && ekJivProfile && (
        <div className="absolute bottom-4 left-0 right-0 flex flex-col items-center z-30">
          <button
            onClick={handleJaiJinendra}
            className={`w-[52px] h-[52px] rounded-full bg-[#C857A8] flex items-center justify-center ${jjUsed ? 'opacity-60' : ''}`}
            style={{ boxShadow: '0 4px 20px rgba(200,87,168,0.3)' }}
          >
            {jjUsed ? <Lock className="h-5 w-5 text-white" /> : <Star className="h-5 w-5 text-white fill-white" />}
          </button>
          <p className="text-[11px] text-[#A8A5A0] mt-2">Send Jai Jinendra to connect</p>
        </div>
      )}
    </div>
  );
}
